//src/pages/cadastros/UnitsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { OperationalClientRequired } from "@/components/shared/OperationalClientRequired";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useSetUnitActive, useUnits } from "@/hooks/useUnits";
import type { UnitListItem } from "@/services/units";
import { maskCNPJ, onlyDigits } from "@/lib/masks";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";

type StatusFilter = "active" | "inactive" | "all";

const LIMIT = 50;

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

export default function UnitsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientCompanyId } = useSelectedClientCompany();

  const userPermissions = user?.permissions ?? [];
  const canReadUnits = userPermissions.includes(PERMISSIONS.UNITS_READ);
  const canWriteUnits = userPermissions.includes(PERMISSIONS.UNITS_WRITE);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<UnitListItem | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statusFilter]);

  const query = useUnits({
    q: debouncedSearch,
    limit: LIMIT,
    offset,
  });

  const setActiveMut = useSetUnitActive();
  const allRows = query.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(debouncedSearch);

    let base = allRows;
    if (statusFilter === "active") base = base.filter((r) => r.is_active);
    else if (statusFilter === "inactive") base = base.filter((r) => !r.is_active);

    if (!q) return base;

    return base.filter((r) => {
      const name = normalizeText(r.name);
      const legalName = normalizeText(r.legal_name ?? "");
      const doc = normalizeText(r.document_number ?? "");
      const tenantName = normalizeText(r.tenant_name ?? "");
      const tenantDoc = normalizeText(r.tenant_document_number ?? "");

      return (
        name.includes(q) ||
        legalName.includes(q) ||
        doc.includes(q) ||
        tenantName.includes(q) ||
        tenantDoc.includes(q) ||
        onlyDigits(doc).includes(onlyDigits(q)) ||
        onlyDigits(tenantDoc).includes(onlyDigits(q))
      );
    });
  }, [allRows, debouncedSearch, statusFilter]);

  const hasNextPage = allRows.length === LIMIT;
  const canPrev = offset > 0;
  const isBusy = query.isFetching || setActiveMut.isPending;

  function openToggle(row: UnitListItem) {
    if (!canWriteUnits) return;
    setSelected(row);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteUnits) return;

    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });

      toast.success(nextActive ? "Unidade ativada" : "Unidade desativada");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status da unidade");
    }
  }

  if (!selectedClientCompanyId) {
    return (
      <div className="pb-10">
        <OperationalClientRequired />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Unidades"
        description="Cadastro de filiais, matriz, fábricas e demais unidades"
      >
        <PermissionButton
          permission={PERMISSIONS.UNITS_WRITE}
          onClick={() => navigate("/cadastros/unidades/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nova Unidade
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por unidade, empresa ou CNPJ."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={!canPrev || query.isFetching}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + LIMIT)}
            disabled={!hasNextPage || query.isFetching}
          >
            Próxima
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando unidades...
        </Card>
      ) : query.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(query.error as any)?.message || "Erro ao carregar unidades"}
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => query.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Nenhuma unidade encontrada"
          description="Cadastre uma unidade ou revise os filtros aplicados."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unidade</TableHead>
                <TableHead>CNPJ Unidade</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>CNPJ Empresa</TableHead>
                <TableHead>Funcionários</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[140px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{r.name}</span>
                      {r.legal_name ? (
                        <span className="text-xs text-muted-foreground">
                          {r.legal_name}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {r.document_number ? maskCNPJ(r.document_number) : "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {r.tenant_name || "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {r.tenant_document_number ? maskCNPJ(r.tenant_document_number) : "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {r.employee_count}
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={r.is_active ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {r.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/unidades/${r.id}`)}
                      onEdit={() => navigate(`/cadastros/unidades/${r.id}/editar`)}
                      onToggleActive={() => openToggle(r)}
                      isActive={r.is_active}
                      disabled={isBusy}
                      viewPermission={PERMISSIONS.UNITS_READ}
                      editPermission={PERMISSIONS.UNITS_WRITE}
                      toggleActivePermission={PERMISSIONS.UNITS_WRITE}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Unidade" : "Ativar Unidade"}
        description={
          selected?.is_active
            ? "Deseja desativar esta unidade?"
            : "Deseja ativar esta unidade novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
